/**
 * What a back-off descent will WEIGH — the ~estimate the matrix draws beside its "% of the top".
 *
 * A descent carries no authored weight: its load is derived during the session from the top set's
 * actual logged kilograms (plan-progression R10/R11), which is why the set table shows a percent and
 * the athlete's screen shows nothing until the top is logged. The BUILDER is a different question:
 * the trainer is looking at a top whose prescription they just typed, and "85% of the top" without a
 * number beside it makes a ladder impossible to sanity-check. So the matrix previews the descent at
 * the top's own projected weight — marked as an estimate, never as a prescription.
 *
 * ── This is the ONLY place this app runs the in-session policy, and it is fixture-pinned ──────
 * Two rounding grids exist and they are not interchangeable (see `plateRounding.ts`). This module
 * runs the **workout-start / in-session** one — the 2.5 kg plate step dropping to 1.25 below 30 kg,
 * with the ±50% sanity clamp — read as DATA from the vendored
 * `__fixtures__/prescription-contract.json`. Nothing here restates a constant: plan-progression R5
 * and R12 say the derivation policy is defined once on the backend and shipped to consumers, and a
 * copy typed into this file would be a second definition that agrees until it does not.
 *
 * `prescriptionContract.test.ts` replays every case of the contract's `roundToPlates`,
 * `clampToReference` and `deriveBackoff` blocks through these three functions.
 *
 * ── Ported from `move-with-me-frontend/.../active-workout/lib/backoffTarget.ts` ───────────────
 * The three maths functions and the half-away-from-zero helper are byte-faithful to mobile's, which
 * are themselves a port of `Services/Progression/PrescriptionMath.cs`. One difference in FRAMING
 * rather than in behaviour: mobile reads its policy off the workout-start payload, because the
 * logger is handed one; the builder never starts a workout, so the vendored contract is the only
 * honest source here. Mobile's `ladderLoadForSet` state machine — awaiting the top, unresolved,
 * derived — is deliberately NOT ported: it answers a question about a session in progress, and the
 * builder has none.
 *
 * ── The estimate is suppressed rather than fabricated ────────────────────────────────────────
 * `deriveBackoffWeight` is TOTAL and answers `null` for a null, zero or negative top and for a null
 * percent. Every one of those means the top's weight is not knowable at authoring time — a dynamic
 * rule that resolves at workout start, a bodyweight movement, a %1RM prescription with no client max
 * — and R11's rule is that a row shows its percent rather than a fabricated number. The matrix
 * renders the percent alone in exactly those cases.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import contract from './__fixtures__/prescription-contract.json';

/**
 * The R5 derivation constants. Read from the vendored contract rather than declared, so the only
 * copy in this repo is the backend's own emitted file.
 */
export interface DerivationPolicy {
  readonly plateStep: number;
  readonly microPlateStep: number;
  readonly microPlateThreshold: number;
  readonly clampLow: number;
  readonly clampHigh: number;
}

/** The single production policy, as the backend emitted it. */
export const DERIVATION_POLICY: DerivationPolicy = contract.policy;

/** `Math.round` rounds half toward +∞; the backend rounds half AWAY FROM ZERO. They differ below 0. */
function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * Clamp a resolved weight to `[|reference| × clampLow, |reference| × clampHigh]` — the ±50% sanity
 * guard around the reference it came from, so a mistyped top cannot push a descent to an extreme.
 *
 * The MAGNITUDE, not the signed value: the contract pins a negative reference of −100 leaving 85
 * untouched, which only holds because the bounds are built from `|−100|`. Not plate-aligned, so
 * callers round AFTER clamping — clamping last would park the weight on a bound no bar can hold.
 */
export function clampToReference(
  resolved: number,
  reference: number,
  policy: DerivationPolicy = DERIVATION_POLICY,
): number {
  const magnitude = Math.abs(reference);
  return Math.min(Math.max(resolved, magnitude * policy.clampLow), magnitude * policy.clampHigh);
}

/**
 * Round to the nearest loadable plate: the full plate step, dropping to the micro-plate step below
 * the threshold where small plates apply. The step is chosen from the INPUT weight, which is what
 * makes 31.25 round up to 32.5 while 22 rounds up to 22.5.
 */
export function roundToPlates(
  weight: number,
  policy: DerivationPolicy = DERIVATION_POLICY,
): number {
  const step = weight < policy.microPlateThreshold ? policy.microPlateStep : policy.plateStep;
  return roundHalfAwayFromZero(weight / step) * step;
}

/**
 * The descent's weight: percent × top, clamped to the top, rounded to a plate, floored positive.
 *
 * TOTAL, and never throws — a null or non-positive top, or a null percent, answers `null` rather
 * than letting the positivity floor fabricate a micro-plate out of nothing. The floor guards only a
 * legitimately resolved positive reference.
 */
export function deriveBackoffWeight(
  topReferenceWeight: number | null | undefined,
  percent: number | null | undefined,
  policy: DerivationPolicy = DERIVATION_POLICY,
): number | null {
  if (topReferenceWeight == null || topReferenceWeight <= 0 || percent == null) return null;
  const rounded = roundToPlates(
    clampToReference(topReferenceWeight * percent, topReferenceWeight, policy),
    policy,
  );
  return rounded > 0 ? rounded : policy.microPlateStep;
}
